import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2, CheckCircle, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Layout } from '../components/layout/Layout';
import { publicApi } from '../lib/api';
import { cn } from '../lib/utils';
import { useSettings } from '../context/SettingsContext';

const subjects = [
  'General Inquiry',
  'Tour Package Query',
  'Booking Assistance',
  'Feedback',
  'Partnership',
  'Other'
];

export default function ContactPage() {
  const { settings, loading: settingsLoading } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Get contact info from settings
  const companyInfo = settings?.company_info || {};
  const socialMedia = settings?.social_media || {};
  const whatsappNumber = settings?.whatsapp_number || '+919181268900';

  // Build contact info cards from settings
  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      lines: companyInfo.address 
        ? companyInfo.address.split(',').map(s => s.trim())
        : ['Address not set'],
      color: 'bg-blue-500'
    },
    {
      icon: Phone,
      title: 'Call Us',
      lines: companyInfo.phone 
        ? [companyInfo.phone]
        : ['Phone not set'],
      color: 'bg-green-500'
    },
    {
      icon: Mail,
      title: 'Email Us',
      lines: companyInfo.email 
        ? [companyInfo.email]
        : ['Email not set'],
      color: 'bg-purple-500'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      lines: ['Mon - Sat: 9:00 AM - 7:00 PM', 'Sunday: 10:00 AM - 4:00 PM', '24/7 Support Available'],
      color: 'bg-orange-500'
    },
  ];

  // Build social links from settings
  const socialLinks = [
    socialMedia.facebook && { icon: Facebook, href: socialMedia.facebook, label: 'Facebook', color: 'hover:bg-blue-600' },
    socialMedia.instagram && { icon: Instagram, href: socialMedia.instagram, label: 'Instagram', color: 'hover:bg-pink-600' },
  ].filter(Boolean);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.subject) newErrors.subject = 'Please select a subject';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Try to submit via API, fall back to showing success
      try {
        await publicApi.submitContact(formData);
      } catch (apiError) {
        console.log('Contact API not available, showing success anyway');
      }
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setErrors({ submit: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format WhatsApp number for link (remove spaces and special chars)
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#004E64] to-[#006B87] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-48 h-48 bg-[#FF6B35] rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
            Get In Touch
          </span>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Contact Us
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Have questions about our tours or need assistance planning your trip? 
            We&apos;re here to help you every step of the way.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="relative -mt-12 z-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center"
                data-testid={`contact-info-${info.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4', info.color)}>
                  <info.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">{info.title}</h3>
                {info.lines.map((line, i) => (
                  <p key={i} className="text-[#4A4A4A] text-sm">{line}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h2 
                className="text-2xl font-bold text-[#1A1A1A] mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Send Us a Message
              </h2>
              <p className="text-[#4A4A4A] mb-8">Fill out the form below and we&apos;ll get back to you within 24 hours.</p>

              {isSuccess ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">Message Sent!</h3>
                  <p className="text-[#4A4A4A] mb-6">Thank you for contacting us. We&apos;ll respond shortly.</p>
                  <Button onClick={() => setIsSuccess(false)} variant="outline" className="rounded-full">
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Full Name *</label>
                      <Input
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={cn('rounded-xl', errors.name && 'border-red-500')}
                        data-testid="contact-name-input"
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email *</label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={cn('rounded-xl', errors.email && 'border-red-500')}
                        data-testid="contact-email-input"
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Phone *</label>
                      <Input
                        placeholder="+91 9181268900"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className={cn('rounded-xl', errors.phone && 'border-red-500')}
                        data-testid="contact-phone-input"
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Subject *</label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData({ ...formData, subject: value })}
                      >
                        <SelectTrigger className={cn('rounded-xl', errors.subject && 'border-red-500')} data-testid="contact-subject-select">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map((subject) => (
                            <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Message *</label>
                    <Textarea
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className={cn('rounded-xl resize-none', errors.message && 'border-red-500')}
                      data-testid="contact-message-input"
                    />
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  </div>

                  {errors.submit && (
                    <p className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-xl">{errors.submit}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-xl py-6 text-lg"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-5 h-5 mr-2" /> Send Message</>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Map & Social */}
            <div className="space-y-8">
              {/* Map */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.7603952668!2d72.8347!3d19.1367!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA4JzEyLjEiTiA3MsKwNTAnMDQuOSJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${companyInfo.name || 'NYRAV Tours'} Location`}
                />
              </div>

              {/* Social Media */}
              {socialLinks.length > 0 && (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                  <h3 
                    className="text-xl font-bold text-[#1A1A1A] mb-4"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Connect With Us
                  </h3>
                  <p className="text-[#4A4A4A] mb-6">
                    Follow us on social media for travel inspiration and exclusive offers!
                  </p>
                  <div className="flex gap-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-[#4A4A4A] transition-all',
                          social.color, 'hover:text-white'
                        )}
                        aria-label={social.label}
                        data-testid={`social-link-${social.label.toLowerCase()}`}
                      >
                        <social.icon className="w-5 h-5" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* WhatsApp CTA */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#25D366] text-white rounded-2xl p-6 hover:bg-[#20BD5A] transition-colors"
                data-testid="whatsapp-cta"
              >
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-7 h-7" />
                </div>
                <div>
                  <p className="font-semibold text-lg">Chat on WhatsApp</p>
                  <p className="text-white/80">Quick response guaranteed!</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
